/**
 * Keep Net Checkout PIN request bodies out of captured error reports.
 *
 * PUT /campaign/<id>/net-pin carries the PIN in its body
 * ({pin, terms_version}). The browser Sentry SDK does not attach request
 * bodies on its own, but an axios error handed to it carries ``config.data``
 * (the serialized body) next to ``config.url``, and a request, breadcrumb or
 * extra can name that URL. Three rules, applied in place over the event:
 *
 *  1. any object addressed to a /net-pin URL loses its body fields; any
 *     request config (url beside headers) loses its Authorization header,
 *     including headers.common (an axios error's config carries both);
 *  2. ANY value under a key named ``pin`` is redacted wherever it turns up:
 *     the PUT body is {pin, terms_version}, and the storefront verify body is
 *     a bare {pin}, so the key, not the shape, is the rule;
 *  3. a string holding such a body as JSON has the pin value redacted,
 *     including a body that was JSON-encoded twice (``\\"pin\\":\\"1234\\"``).
 *
 * Everything else is left untouched (it is a scrub, not a blanket). Leaf
 * module, no imports: main.js composes it with the resume-code scrub.
 */

export const NET_PIN_REDACTED = "[redacted]";

// ``.../net-pin`` as a whole path segment, followed by nothing, a deeper
// segment, a query or a fragment.
const NET_PIN_URL = /\/net-pin(?:[/?#]|$)/;
// Keys a request/error object uses for its body.
const BODY_KEYS = ["data", "body", "request_body", "requestBody"];
// ``"pin":"..."`` as it appears in a JSON string, with up to three levels of
// backslash escaping around the quotes (double-encoded bodies). The value is a
// BOUNDED lazy run (a PIN is at most 64 characters; 160 leaves room for
// escapes) to the closing quote, so a string full of unterminated ``"pin":"``
// cannot trigger quadratic scanning: each occurrence costs at most ~160 steps.
const JSON_PIN = /(\\{0,3}"pin\\{0,3}"\s{0,8}:\s{0,8}\\{0,3}")[^"]{0,160}?(?=\\{0,3}")/gi;
// ``"pin": 123456`` (a number), also double-encoded.
const JSON_PIN_NUMBER = /(\\{0,3}"pin\\{0,3}"\s{0,8}:\s{0,8})-?\d{1,64}(?:\.\d{1,16})?/gi;
// A string longer than this that mentions a pin key is not scanned: it is
// replaced whole (an event field that large is not a request body worth keeping).
const MAX_SCAN_LENGTH = 200000;
const OVERSIZED = "[redacted: oversized string mentioning a pin]";

function redactString(value) {
  if (!/pin/i.test(value)) return value;
  if (value.length > MAX_SCAN_LENGTH) return /"pin/i.test(value) || /\\"pin/i.test(value) ? OVERSIZED : value;
  return value
    .replace(JSON_PIN, `$1${NET_PIN_REDACTED}`)
    .replace(JSON_PIN_NUMBER, `$1"${NET_PIN_REDACTED}"`);
}

function dropAuthorization(headers, depth = 0) {
  if (!headers || typeof headers !== "object" || depth > 2) return;
  for (const name of Object.keys(headers)) {
    try {
      if (name.toLowerCase() === "authorization") delete headers[name];
      else dropAuthorization(headers[name], depth + 1); // headers.common etc.
    } catch {
      // frozen: leave it
    }
  }
}

export function scrubNetPinRequest(value, seen = new WeakSet()) {
  if (typeof value === "string") return redactString(value);
  if (!value || typeof value !== "object" || seen.has(value)) return value;
  seen.add(value);

  const addressed = typeof value.url === "string" && NET_PIN_URL.test(value.url);

  for (const key of Object.keys(value)) {
    try {
      if (addressed && BODY_KEYS.includes(key)) {
        delete value[key];
      } else if (key.toLowerCase() === "pin" && value[key] !== null && value[key] !== undefined) {
        // Any value (string, number, array of digits) under a pin key.
        value[key] = NET_PIN_REDACTED;
      } else {
        value[key] = scrubNetPinRequest(value[key], seen);
      }
    } catch {
      // A frozen or getter-only property: leave it rather than drop the
      // whole event.
    }
  }
  // Any request config (a url beside headers) loses its Authorization: the
  // bearer token has no business in a report, and a /net-pin error carries it.
  if (typeof value.url === "string" && value.headers && typeof value.headers === "object") {
    dropAuthorization(value.headers);
  }
  return value;
}
